/**
 * UX store (user interface settings and other temporary data)
 */
import { getDefaultLocale, checkLocaleSupport } from '@/helpers/i18n.js';
import { isTestEnv } from '@/helpers/deployment.js';

export const phaseInitial = -1;
export const phaseAnonymous = 0;
export const phaseEmailUnverified = 15;
export const phaseEmailVerified = 25;
export const phaseExpired = 50;
export const phaseBusy = 250;
export const phaseAuthenticated = 500;
export const phaseTOSUpdate = 750;
export const phaseProfile = 1000;
export const phaseComplete = 5000;

const initialState = () => ({

    /**
     * UI version tag
     */
    uiTag: null,

    /**
        LoginPhase

               -1       Initial             anonymous, first visit on the site
                0       Anonymous           anonymous
                15      EmailUnverified     registration email sent, email not verified, not logged in
                25      EmailVerified       email verified, not logged in
                50      Expired             login expired
                250     Busy                logging process initiated, waiting for user to return from auth0
                500     Authenticated       authenticated, waiting for further profile data
                750     TOSUpdate           TOS update, needs user review (user profile available)
                1000    Profile             user profile available, TOS accepted
                5000    Complete            user profile items available

     */
    loginPhase: phaseInitial,

    locale: getDefaultLocale(),

    /**
     * Site theme.
     */
    theme: 'light',

    editMode: false,
    hasUnsavedEdits: false,

    /**
     * The "UseProfile" request param to switch view scope.
     *
     * As enabling edit mode will also switch UseProfile,
     * the previous state is also stored.
     */
    useProfile: 'ViewPublicOnly',
    previousUseProfile: 'ViewPublicOnly',

    /**
     * Home front-page loop and cover indices
     */
    frontPageHeroIndex: 0,
    frontPageHeroIndexTime: new Date().getTime(),
    frontPageCoverIndex: 0,

    /**
     * User might see in interesting artwork and click on it,
     * but the resulting page does not display it anymore.
     * This store value is used to save the clicked image.
     */
    contextArtwork: null,

    showRegistrationInfo: true,

    communityAllowAnon: false,
    communityShowNavigation: false

});

const createStore = (app) => {

    const state = initialState;

    const getters = {

        getUiTag (state) {
            return state.uiTag;
        },

        getCommunityAllowAnon (state) {
            return state.communityAllowAnon;
        },

        getCommunityShowNavigation (state) {
            return state.communityShowNavigation;
        },

        getLocale (state) {
            return state.locale;
        },

        getTheme (state) {
            return state.theme;
        },

        getLoginPhase (state) {
            return state.loginPhase;
        },

        getEditMode (state) {
            return state.editMode;
        },

        getHasUnsavedEdits (state) {
            return state.hasUnsavedEdits;
        },

        getUseProfile (state) {
            return state.useProfile;
        },

        getFrontPageHeroIndex (state) {
            return state.frontPageHeroIndex;
        },

        getFrontPageHeroIndexTime (state) {
            return state.frontPageHeroIndexTime;
        },

        getFrontPageCoverIndex (state) {
            return state.frontPageCoverIndex;
        },

        getContextArtwork (state) {
            return state.contextArtwork;
        },

        getShowRegistrationInfo (state) {
            return state.showRegistrationInfo;
        }
    };

    const actions = {

        /**
         * True when store was initialised with current frontend ui version tag.
         * Throws error when stores ought to be cleared.
         */
        checkUiTag ({ commit, state }, currentUiTag) {
            if (state.uiTag === currentUiTag || !state.uiTag || !currentUiTag || isTestEnv) {
                if (state.uiTag !== currentUiTag) {
                    commit('setUiTag', currentUiTag);
                }
                // no full localStorage reset
                return Promise.resolve();
            }
            else {
                // App should reset the stores
                return Promise.reject(new Error("Outdated uiTag"));
            }
        }
    };

    const mutations = {

        RESET (state) {
            const newState = initialState();
            Object.keys(newState).forEach((key) => {
                state[key] = newState[key];
            });
        },

        setUiTag (state, value) {
            state.uiTag = value;
        },

        setCommunityAllowAnon (state, value) {
            state.communityAllowAnon = value;
        },

        setCommunityShowNavigation (state, value) {
            state.communityShowNavigation = value;
        },

        /**
         * Set locale only if supported
         */
        setLocale (state, value) {
            if (checkLocaleSupport(value)) {
                state.locale = value;
            }
        },

        resetLocale (state) {
            state.locale = getDefaultLocale();
        },

        setTheme (state, value) {
            state.theme = value;
        },

        setLoginPhase (state, value) {
            state.loginPhase = value;
            if (state.showRegistrationInfo && value > phaseAnonymous) {
                state.showRegistrationInfo = false;
            }
        },

        setEditMode (state, value) {
            state.editMode = value;
        },

        setHasUnsavedEdits (state, value) {
            state.hasUnsavedEdits = value;
        },

        setUseProfile (state, value) {
            state.previousUseProfile = state.useProfile;
            state.useProfile = value;
        },

        restoreUseProfile (state) {
            state.useProfile = state.previousUseProfile;
        },

        setFrontPageHeroIndex (state, value) {
            state.frontPageHeroIndex = value;
        },

        setFrontPageHeroIndexTime (state, value) {
            state.frontPageHeroIndexTime = value;
        },

        setFrontPageCoverIndex (state, value) {
            state.frontPageCoverIndex = value;
        },

        setContextArtwork (state, value) {
            state.contextArtwork = value;
        },

        setShowRegistrationInfo (state, value) {
            state.showRegistrationInfo = value;
        }
    };

    return {
        namespaced: true,
        state,
        getters,
        actions,
        mutations
    };
};

export default app => createStore(app);
